import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(() => {
    if (!slug) return [];
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const pagedArticles = filteredArticles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h2>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 丝袜小说精选合集`,
    description: category.description,
    url: `https://vipmvpda.cn/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: pagedArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://vipmvpda.cn/article/${article.id}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 丝袜小说精选合集`}
        description={`${category.description}，丝袜小说精选合集为您提供最新最全的${category.name}类丝袜小说，每日更新最新章节，免费在线阅读。`}
        keywords={`丝袜小说,${category.name},丝袜${category.name},免费丝袜小说,丝袜小说在线阅读`}
        canonicalUrl={`https://vipmvpda.cn/category/${slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>

      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <div className="bg-background-100 pb-6">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {pagedArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {pagedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="default" />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-200 ${
                        page === currentPage
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无文章</p>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}